import React, { useEffect, useState } from "react";
import { ChevronLeft, Gamepad2, Play } from "lucide-react";
import { Modal } from "@/components/Modal";
import { sfx } from "@/lib/soundfx";
import { GAMES, GAME_BY_KEY, GROUPS, GROUP_COLOR } from "./registry";
import { GameErrorBoundary } from "./GameErrorBoundary";
import { ConsoleFrame } from "./ConsoleFrame";

const GROUP_LABEL = { Mohalla: "🏘️ Mohalla Games", Classroom: "🏫 Classroom Games", Timepass: "✨ Everyone Played", Arcade: "🕹️ Video Games", Board: "🎲 Board Games" };

export const GameArcade = ({ open, onClose, initialKey = null, groups = GROUPS, title = "Games Corner", subtitle }) => {
  const [key, setKey] = useState(initialKey);
  const [playing, setPlaying] = useState(false);
  useEffect(() => { if (open) { setKey(initialKey); setPlaying(false); } }, [open, initialKey]);

  if (!open) return null;
  const game = key ? GAME_BY_KEY[key] : null;
  const Game = game?.C;
  const accent = game ? GROUP_COLOR[game.group] : "#8a5a2b";
  const visibleGroups = GROUPS.filter((g) => groups.includes(g));

  const backToIndex = () => { setKey(null); setPlaying(false); };

  return (
    <Modal open={open} onClose={onClose} testId="game-arcade" maxWidth="max-w-2xl">
      <div className="p-5 sm:p-7">
        {game ? (
          <div>
            <div className="mb-4 flex items-center gap-3">
              <button data-testid="arcade-back" onClick={playing ? () => setPlaying(false) : backToIndex}
                className="flex items-center gap-1 rounded-md border-2 border-kirana-brown bg-kirana-cream px-3 py-1.5 text-sm font-bold text-kirana-brown transition-colors hover:bg-kirana-gold">
                <ChevronLeft className="h-4 w-4" /> {playing ? "Back" : "All Games"}
              </button>
              <h2 className="font-signboard text-2xl font-bold leading-none text-kirana-brown sm:text-3xl">
                {game.emoji} {game.name}
              </h2>
            </div>

            {playing ? (
              // colored highlight boundary around the play area, per group
              <div data-testid={`arcade-game-${game.key}`} className="rounded-xl p-1" style={{ background: accent }}>
                <div className="rounded-lg border-2 bg-kirana-cream/40 p-3 sm:p-4" style={{ borderColor: accent }}>
                  <GameErrorBoundary key={game.key}>
                    {game.group === "Arcade" ? <ConsoleFrame><Game /></ConsoleFrame> : <Game />}
                  </GameErrorBoundary>
                </div>
              </div>
            ) : (
              // PLAY button on top, then rules + description
              <div data-testid={`arcade-card-${game.key}`} className="rounded-xl p-1" style={{ background: accent }}>
                <div className="rounded-lg border-2 bg-card p-5" style={{ borderColor: accent }}>
                  <button data-testid="arcade-play-btn" onClick={() => { sfx.bell(); setPlaying(true); }}
                    className="mb-4 flex w-full items-center justify-center gap-2 rounded-md border-[3px] border-kirana-brown px-6 py-3 text-lg font-bold text-white transition-transform hover:scale-[1.02] hard-shadow"
                    style={{ background: accent }}>
                    <Play className="h-5 w-5 fill-white" /> PLAY NOW
                  </button>
                  <div className="flex items-center gap-2">
                    <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border-[3px] text-2xl" style={{ borderColor: accent }}>{game.emoji}</span>
                    <span className="inline-block rounded-full border-2 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide" style={{ borderColor: accent, color: accent }}>{GROUP_LABEL[game.group]}</span>
                  </div>
                  <div className="mt-3 rounded-md border-2 border-dashed border-kirana-border bg-kirana-cream/60 p-3 text-left">
                    <p className="mb-1 text-xs font-bold uppercase text-kirana-brown/70">Rules · How to play</p>
                    <ol className="list-inside list-decimal space-y-1 text-sm text-kirana-brown">
                      {game.how.map((s, i) => <li key={i}>{s}</li>)}
                    </ol>
                  </div>
                  <p className="mt-3 text-sm italic text-kirana-brown">{game.desc}</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="mb-1 flex items-center gap-2">
              <span className="flex h-10 w-10 items-center justify-center rounded-full border-[3px] border-kirana-brown bg-kirana-gold">
                <Gamepad2 className="h-5 w-5 text-kirana-brown" />
              </span>
              <div>
                <h2 className="font-signboard text-3xl font-bold leading-none text-kirana-brown">{title}</h2>
                <p className="text-xs text-kirana-brown/70">{subtitle || "Tap a game to see how it's played, then play"}</p>
              </div>
            </div>
            {visibleGroups.map((grp) => (
              <div key={grp} className="mt-5">
                <h3 className="mb-2 font-signboard text-lg font-bold" style={{ color: GROUP_COLOR[grp] }}>{GROUP_LABEL[grp]}</h3>
                <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                  {GAMES.filter((g) => g.group === grp).map((g) => (
                    <button key={g.key} data-testid={`arcade-tile-${g.key}`} onClick={() => setKey(g.key)}
                      className="flex flex-col items-start gap-0.5 rounded-lg border-[3px] bg-card p-3 text-left transition-transform hover:-translate-y-0.5 hover:bg-kirana-cream hard-shadow-sm"
                      style={{ borderColor: GROUP_COLOR[grp] }}>
                      <span className="text-2xl">{g.emoji}</span>
                      <span className="font-signboard text-base font-bold leading-tight text-kirana-brown">{g.name}</span>
                      <span className="text-[11px] leading-tight text-kirana-brown/60">{g.tag}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};
